/**
 * Text ⇄ ProseMirror-doc serialization for VariableTextInput — a var-token-only
 * variant of the prompt editor's serializer (no `#kind/slug` mentions). The
 * stored form is plain text with `{{…}}` tokens; the editor renders a doc of
 * paragraphs containing text and atomic `varToken` chips. The round-trip is the
 * invariant the input relies on to stay diffable text:
 *
 *     docToText(textToDoc(text)) === text   for all text
 *
 * Pure functions over plain ProseMirror JSON, so they unit-test without an
 * editor; the TipTap component feeds them `editor.getJSON()` / `setContent`.
 */

export interface PMText {
  type: "text";
  text: string;
}
export interface PMVarToken {
  type: "varToken";
  attrs: { token: string };
}
export type PMInline = PMText | PMVarToken;
export interface PMParagraph {
  type: "paragraph";
  content?: PMInline[];
}
export interface PMDoc {
  type: "doc";
  content: PMParagraph[];
}

/** `{{anything}}` — the card binding family ({{slot}}, {{item.x}}, {{state.x}},
 *  {{t:key}}). Disallows nested braces so it never swallows past a token. */
const VAR_TOKEN = /\{\{[^{}]+\}\}/g;

/** Canonical `{{inner}}` form with surrounding whitespace trimmed, so a stored
 *  `{{ state.size }}` and an inserted `{{state.size}}` resolve to one label key. */
export function normalizeToken(raw: string): string {
  const m = /^\{\{\s*([\s\S]*?)\s*\}\}$/.exec(raw);
  return m ? `{{${m[1]}}}` : raw;
}

function lineToInline(line: string): PMInline[] {
  const out: PMInline[] = [];
  let cursor = 0;
  VAR_TOKEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = VAR_TOKEN.exec(line))) {
    if (m.index > cursor) out.push({ type: "text", text: line.slice(cursor, m.index) });
    out.push({ type: "varToken", attrs: { token: m[0] } });
    cursor = m.index + m[0].length;
  }
  if (cursor < line.length) out.push({ type: "text", text: line.slice(cursor) });
  return out;
}

export function textToDoc(text: string): PMDoc {
  const lines = (text ?? "").split("\n");
  return {
    type: "doc",
    content: lines.map((line) => {
      const content = lineToInline(line);
      // ProseMirror paragraphs omit empty content arrays.
      return content.length ? { type: "paragraph", content } : { type: "paragraph" };
    }),
  };
}

function inlineToText(node: PMInline): string {
  return node.type === "text" ? node.text : node.attrs.token;
}

export function docToText(doc: PMDoc): string {
  return (doc.content ?? []).map((p) => (p.content ?? []).map(inlineToText).join("")).join("\n");
}
